import React, { useContext, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import productData from "../Datas/productData";
import Footer from "./Footer";
import "../css/productdetails.scss";
import { CartContext } from "./CartContext";
import Header from "./Header";
import {
  FaStar,
  FaShippingFast,
  FaShieldAlt,
  FaCheck,
  FaChevronRight,
  FaChevronDown,
} from "react-icons/fa";

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const product = productData.find((item) => item.id === parseInt(id));
  const { addToCart } = useContext(CartContext);

  const [selectedImage, setSelectedImage] = useState(0);
  const [activeTab, setActiveTab] = useState("features");
  const [quantity, setQuantity] = useState(1);

  const toTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  if (!product) {
    return (
      <div className="product-detail">
        <Header />
        <div className="product-detail__not-found">
          <h2>Product not found</h2>
          <Link onClick={toTop} to="/shop">
            Back to Shop
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  // Get related products (same category, excluding current product)
  const relatedProducts = productData
    .filter(
      (item) => item.category === product.category && item.id !== product.id
    )
    .slice(0, 4);

  // Render star rating
  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<FaStar key={`full-${i}`} className="star filled" />);
    }

    if (hasHalfStar) {
      stars.push(<FaStar key="half" className="star half" />);
    }

    const emptyStars = 5 - stars.length;
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<FaStar key={`empty-${i}`} className="star empty" />);
    }

    return stars;
  };

  const handleAddToCart = () => {
    const cartItem = {
      ...product,
      quantity: quantity,
      selectedSize: product.size?.[0] || "One size",
      selectedColor: product.category || "Default",
    };
    addToCart(cartItem);
    navigate("/cartpage");
    toTop();
  };

  const handleBuyNow = () => {
    const cartItem = {
      ...product,
      quantity: quantity,
      selectedSize: product.size?.[0] || "One size",
      selectedColor: product.category || "Default",
    };
    addToCart(cartItem);
    navigate("/checkout");
    toTop();
  };

  return (
    <div className="product-detail">
      <Header />

      {/* Main Content */}
      <div className="product-detail__content">
        {/* Breadcrumb */}
        <div className="breadcrumb">
          <Link to="/">Home</Link>
          <FaChevronRight className="breadcrumb-separator" />
          <Link to="/shop">Shop</Link>
          <FaChevronRight className="breadcrumb-separator" />
          <Link to="/shop">{product.category}</Link>
          <FaChevronRight className="breadcrumb-separator" />
          <span className="current">{product.name}</span>
        </div>

        {/* Product Main Section */}
        <div className="product-main">
          {/* Product Images */}
          <div className="product-images">
            {/* Main Image */}
            <div className="main-image">
              <img
                src={product.images[selectedImage] || product.image}
                alt={product.name}
                onError={(e) => {
                  e.target.src =
                    "https://via.placeholder.com/500x500?text=No+Image";
                }}
              />
            </div>
            {/* Thumbnail Images */}
            <div className="thumbnail-images">
              {product.images.map((img, index) => (
                <div
                  key={index}
                  className={`thumbnail ${
                    selectedImage === index ? "active" : ""
                  }`}
                  onClick={() => setSelectedImage(index)}
                >
                  <img
                    src={img}
                    alt={`${product.name} view ${index + 1}`}
                    onError={(e) => {
                      e.target.src =
                        "https://via.placeholder.com/100x100?text=No+Image";
                    }}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Product Info */}
          <div className="product-info">
            {/* Title and Brand */}
            <div className="product-header">
              <h1 className="product-title">{product.name}</h1>
              <p className="product-brand">By hetcratehub</p>
            </div>

            {/* Rating */}
            <div className="product-rating">
              <div className="stars">{renderStars(product.rating)}</div>
              <span className="rating-text">
                {product.rating} out of 5 based on {product.reviews} Ratings
              </span>
            </div>

            {/* Price Section */}
            <div className="product-price-section">
              <div className="price-row">
                <span className="current-price">${product.price}</span>
                <span className="unit">/ each</span>
              </div>
              <p className="price-note">With Autoship, save 5% every time</p>
            </div>

            {/* Stock Status */}
            <div className="stock-status">
              <FaCheck className="check-icon" />
              <span>In Stock</span>
            </div>

            {/* Shipping Info */}
            <div className="shipping-info">
              <div className="shipping-row">
                <FaShippingFast className="shipping-icon" />
                <div className="shipping-text">
                  <span className="shipping-title">Free Shipping</span>
                  <span className="shipping-detail">On orders over $59</span>
                </div>
              </div>
              <div className="shipping-row">
                <FaShieldAlt className="shipping-icon" />
                <div className="shipping-text">
                  <span className="shipping-title">Carefully Selected</span>
                  <span className="shipping-detail">
                    Quality pet essentials
                  </span>
                </div>
              </div>
            </div>

            {/* Add to Cart Section */}
            <div className="add-to-cart-section">
              <div className="quantity-selector">
                <label>Qty:</label>
                <div className="quantity-controls">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="qty-btn"
                  >
                    -
                  </button>
                  <input
                    type="number"
                    value={quantity}
                    onChange={(e) =>
                      setQuantity(Math.max(1, parseInt(e.target.value) || 1))
                    }
                    min="1"
                  />
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="qty-btn"
                  >
                    +
                  </button>
                </div>
              </div>

              <button className="add-to-cart-btn" onClick={handleAddToCart}>
                Add to Cart
              </button>

              <button className="buy-now-btn" onClick={handleBuyNow}>
                Buy Now
              </button>
            </div>
          </div>
        </div>

        {/* Product Details Section */}
        <div className="product-details-section">
          <div className="details-tabs">
            <button
              className={`tab-btn ${activeTab === "features" ? "active" : ""}`}
              onClick={() => setActiveTab("features")}
            >
              Features
              <FaChevronDown className="tab-icon" />
            </button>
            <button
              className={`tab-btn ${
                activeTab === "description" ? "active" : ""
              }`}
              onClick={() => setActiveTab("description")}
            >
              Description
              <FaChevronDown className="tab-icon" />
            </button>
          </div>

          <div className="details-content">
            {activeTab === "features" && (
              <div className="features-content">
                <div className="features-list">
                  <h3>Key Features:</h3>
                  <ul>
                    {product.Features &&
                      product.Features.map((feature, index) => (
                        <li key={index}>
                          <FaCheck className="feature-check" />
                          {feature}
                        </li>
                      ))}
                  </ul>
                </div>
              </div>
            )}

            {activeTab === "description" && (
              <div className="description-content">
                <h3>Product Description:</h3>
                <ul>
                  {product.description &&
                    product.description.map((desc, index) => (
                      <li key={index}>{desc}</li>
                    ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="related-products">
            <h2 className="related-title">You May Also Like</h2>
            <div className="related-grid">
              {relatedProducts.map((item) => (
                <Link
                  key={item.id}
                  to={`/shopDetails/${item.id}`}
                  className="related-card"
                  onClick={toTop}
                >
                  <div className="related-image">
                    <img
                      src={item.image}
                      alt={item.name}
                      onError={(e) => {
                        e.target.src =
                          "https://via.placeholder.com/200x200?text=No+Image";
                      }}
                    />
                  </div>
                  <div className="related-info">
                    <h4 className="related-name">{item.name}</h4>
                    <div className="related-rating">
                      {renderStars(item.rating)}
                    </div>
                    <span className="related-price">${item.price}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default ProductDetail;
